import React, { useRef, useState } from 'react';
import { Box, Building2, Calendar, Check, Save, Signature, Upload, UserCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import api, { ApiError } from '../utils/api';
import { toast } from 'sonner';

/** The subset of an asset the return hand-over needs to display. */
export interface ReturnableAsset {
  id: number;
  name: string;
  tag?: string;
  serialNumber?: string | null;
  status?: string;
  assignedToUser?: { name?: string } | null;
  assignedToDepartment?: { departmentName?: string } | null;
}

export interface ReturnReceiverOption {
  id: string | number;
  name: string;
  jobRole?: string | null;
}

interface AssetReturnFormProps {
  asset: ReturnableAsset;
  /** Who can check and accept the asset back. */
  receivers: ReturnReceiverOption[];
  /** Status the asset goes to once returned (defaults to the asset pool). */
  targetStatus?: string;
  onCancel: () => void;
  /** Called after the return has been saved; the host decides what happens next. */
  onReturned: (assetId: number) => void;
  submitLabel?: string;
  /** Shown while a multi-asset queue is running, e.g. "Asset 2 of 5". */
  progressLabel?: string;
}

/**
 * The asset return protocol, extracted from the Asset Register's Return page so
 * the Archive Person journey can use the same form (signatures, photos,
 * condition notes, receiving person) instead of quietly flipping the assignment.
 *
 * The host owns loading/navigation; this owns the form and the POST to
 * `/api/assets/:id/return`.
 */
export const AssetReturnForm: React.FC<AssetReturnFormProps> = ({
  asset,
  receivers,
  targetStatus = 'Available',
  onCancel,
  onReturned,
  submitLabel = 'Complete Return',
  progressLabel
}) => {
  const [returnedDate, setReturnedDate] = useState(new Date().toISOString().split('T')[0]);
  const [returnedToId, setReturnedToId] = useState('');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [assigneeSignatureType, setAssigneeSignatureType] = useState<'Draw' | 'Type'>('Type');
  const [assigneeSignatureData] = useState('');
  const [assigneeTypedSignature, setAssigneeTypedSignature] = useState('');
  const [isAssigneeDrawing, setIsAssigneeDrawing] = useState(false);

  const [receiverSignatureType, setReceiverSignatureType] = useState<'Draw' | 'Type'>('Type');
  const [receiverSignatureData] = useState('');
  const [receiverTypedSignature, setReceiverTypedSignature] = useState('');
  const [isReceiverDrawing, setIsReceiverDrawing] = useState(false);

  const assigneeCanvasRef = useRef<HTMLCanvasElement>(null);
  const receiverCanvasRef = useRef<HTMLCanvasElement>(null);

  const drawSignature = (
    event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
    canvas: HTMLCanvasElement | null,
    isStart: boolean
  ) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();

    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    if (isStart) {
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const clearCanvas = (canvas: HTMLCanvasElement | null) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setPhotos((previous) => [...previous, ...Array.from(event.target.files as FileList)]);
    }
  };

  const removePhoto = (index: number) => {
    setPhotos((previous) => previous.filter((_, position) => position !== index));
  };

  const uploadPhotos = async (): Promise<string[]> => {
    const uploadedKeys: string[] = [];
    for (const file of photos) {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.post<{ url: string; key: string }>('/api/assets/upload', formData);
      uploadedKeys.push(response.key);
    }
    return uploadedKeys;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!returnedToId) {
      toast.error('Please select the person who checked and accepted the asset');
      return;
    }

    let finalAssigneeSignature = assigneeSignatureData;
    if (assigneeSignatureType === 'Type') {
      finalAssigneeSignature = assigneeTypedSignature.trim();
    } else if (assigneeCanvasRef.current) {
      finalAssigneeSignature = assigneeCanvasRef.current.toDataURL();
    }

    let finalReceiverSignature = receiverSignatureData;
    if (receiverSignatureType === 'Type') {
      if (!receiverTypedSignature.trim()) {
        toast.error('Receiving person signature is required');
        return;
      }
      finalReceiverSignature = receiverTypedSignature.trim();
    } else if (receiverCanvasRef.current) {
      finalReceiverSignature = receiverCanvasRef.current.toDataURL();
    }

    if (!finalReceiverSignature) {
      toast.error('Receiving person signature is required');
      return;
    }

    try {
      setSubmitting(true);
      setUploadingPhotos(photos.length > 0);

      const uploadedPhotoUrls = photos.length > 0 ? await uploadPhotos() : [];

      await api.post(`/api/assets/${asset.id}/return`, {
        returnedDate,
        notes,
        targetStatus,
        returnedToId: parseInt(returnedToId),
        returnPhotos: uploadedPhotoUrls,
        returnSignatureType: assigneeSignatureType,
        returnSignatureData: finalAssigneeSignature,
        receiverSignatureType,
        receiverSignatureData: finalReceiverSignature
      });

      toast.success(`Asset successfully returned and marked as ${targetStatus}`);
      onReturned(asset.id);
    } catch (error: unknown) {
      console.error('Error returning asset:', error);
      // The api wrapper throws ApiError with the server message already extracted.
      toast.error(error instanceof ApiError ? error.message : 'Failed to return asset');
    } finally {
      setSubmitting(false);
      setUploadingPhotos(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* 1. Asset Details (pre-populated, read only) */}
      <section className="bg-white p-5 md:p-6 rounded-2xl border border-gray-100 shadow-sm space-y-5">
        <div className="flex items-center gap-3">
          <Box size={22} className="text-blue-500" />
          <div>
            <h2 className="font-bold text-gray-900 text-base">Asset Details</h2>
            <p className="text-gray-500 text-xs font-medium">Read-only information</p>
          </div>
          {progressLabel && (
            <span className="ml-auto rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
              {progressLabel}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-5 bg-gray-50 p-5 rounded-xl border border-gray-100">
          <div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Asset Name</span>
            <p className="font-bold text-gray-900">{asset.name}</p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Asset ID</span>
            <p className="font-bold text-gray-900">{asset.tag || 'N/A'}</p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Serial Number</span>
            <p className="font-bold text-gray-900">{asset.serialNumber || 'N/A'}</p>
          </div>
          <div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Current Status</span>
            <p className="font-bold text-blue-600">{asset.status || 'Assigned'}</p>
          </div>
          <div className="col-span-2">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Current Assignee</span>
            <div className="flex items-center gap-2">
              {asset.assignedToUser ? (
                <UserCircle size={16} className="text-gray-400" />
              ) : (
                <Building2 size={16} className="text-gray-400" />
              )}
              <p className="font-bold text-gray-900">
                {asset.assignedToUser?.name || asset.assignedToDepartment?.departmentName || 'None'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Return Information */}
      <section className="bg-white p-5 md:p-6 rounded-2xl border border-gray-100 shadow-sm space-y-5">
        <div className="flex items-center gap-3">
          <Calendar size={22} className="text-indigo-500" />
          <div>
            <h2 className="font-bold text-gray-900 text-base">Return Information</h2>
            <p className="text-gray-500 text-xs font-medium">Complete the return handover details</p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                Return Date <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={returnedDate}
                onChange={(event) => setReturnedDate(event.target.value)}
                className="rounded-xl h-11 px-4 border-gray-200 focus:ring-2 focus:ring-blue-500/20"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                Checked &amp; Accepted By <span className="text-red-500">*</span>
              </label>
              <Select value={returnedToId} onValueChange={setReturnedToId}>
                <SelectTrigger className="w-full rounded-xl h-11 px-4 border-gray-200">
                  <SelectValue placeholder="Select receiver..." />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {receivers.map((receiver) => (
                    <SelectItem key={receiver.id} value={receiver.id.toString()}>
                      {receiver.name} ({receiver.jobRole || 'User'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Return Notes / Condition</label>
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              className="rounded-xl min-h-[90px] border-gray-200 p-4"
              placeholder="Describe the condition of the asset upon return..."
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Photos of Return (Optional)</label>
            <div className="flex flex-col gap-4">
              {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 group">
                      <img src={URL.createObjectURL(photo)} alt={`Upload ${index}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="relative">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="w-full h-20 border-2 border-dashed border-gray-200 hover:border-blue-500 rounded-xl flex flex-col items-center justify-center text-gray-500 hover:text-blue-500 transition-colors bg-gray-50">
                  <Upload size={20} className="mb-1" />
                  <span className="text-sm font-bold">Click or drag photos to upload</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6 pt-5 border-t border-gray-100">
            {/* Assignee signature (optional) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Assignee Signature (Optional)</label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={assigneeSignatureType === 'Type' ? 'default' : 'outline'} onClick={() => setAssigneeSignatureType('Type')} className="h-8 rounded-lg text-xs font-bold px-4">Type</Button>
                  <Button type="button" size="sm" variant={assigneeSignatureType === 'Draw' ? 'default' : 'outline'} onClick={() => setAssigneeSignatureType('Draw')} className="h-8 rounded-lg text-xs font-bold px-4">Draw</Button>
                </div>
              </div>
              {assigneeSignatureType === 'Type' ? (
                <Input
                  placeholder="Type full name"
                  value={assigneeTypedSignature}
                  onChange={(event) => setAssigneeTypedSignature(event.target.value)}
                  className="rounded-xl h-11 px-4 border-gray-200 font-[signature] text-xl tracking-wider text-gray-800"
                />
              ) : (
                <div className="relative border border-gray-200 rounded-xl overflow-hidden bg-gray-50 touch-none">
                  <canvas
                    ref={assigneeCanvasRef}
                    width={800}
                    height={200}
                    className="w-full h-[140px] cursor-crosshair"
                    onMouseDown={(event) => {
                      setIsAssigneeDrawing(true);
                      drawSignature(event, assigneeCanvasRef.current, true);
                    }}
                    onMouseMove={(event) => {
                      if (isAssigneeDrawing) drawSignature(event, assigneeCanvasRef.current, false);
                    }}
                    onMouseUp={() => setIsAssigneeDrawing(false)}
                    onMouseLeave={() => setIsAssigneeDrawing(false)}
                    onTouchStart={(event) => {
                      setIsAssigneeDrawing(true);
                      drawSignature(event, assigneeCanvasRef.current, true);
                    }}
                    onTouchMove={(event) => {
                      if (isAssigneeDrawing) drawSignature(event, assigneeCanvasRef.current, false);
                    }}
                    onTouchEnd={() => setIsAssigneeDrawing(false)}
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => clearCanvas(assigneeCanvasRef.current)} className="absolute top-2 right-2 h-8 rounded-lg text-xs font-bold text-gray-500 hover:text-red-500 bg-white shadow-sm border border-gray-100">
                    Clear Pad
                  </Button>
                </div>
              )}
            </div>

            {/* Receiving person signature (required) */}
            <div className="space-y-3 p-5 bg-blue-50/30 border border-blue-100 rounded-2xl">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-gray-900 uppercase tracking-wide">
                  Receiving Person Signature <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={receiverSignatureType === 'Type' ? 'default' : 'outline'} onClick={() => setReceiverSignatureType('Type')} className="h-8 rounded-lg text-xs font-bold px-4">Type</Button>
                  <Button type="button" size="sm" variant={receiverSignatureType === 'Draw' ? 'default' : 'outline'} onClick={() => setReceiverSignatureType('Draw')} className="h-8 rounded-lg text-xs font-bold px-4">Draw</Button>
                </div>
              </div>
              {receiverSignatureType === 'Type' ? (
                <Input
                  placeholder="Type full name of receiver"
                  value={receiverTypedSignature}
                  onChange={(event) => setReceiverTypedSignature(event.target.value)}
                  className="rounded-xl h-11 px-4 border-gray-200 font-[signature] text-xl tracking-wider text-gray-800 bg-white"
                />
              ) : (
                <div className="relative border border-gray-200 rounded-xl overflow-hidden bg-white touch-none">
                  <canvas
                    ref={receiverCanvasRef}
                    width={800}
                    height={200}
                    className="w-full h-[140px] cursor-crosshair"
                    onMouseDown={(event) => {
                      setIsReceiverDrawing(true);
                      drawSignature(event, receiverCanvasRef.current, true);
                    }}
                    onMouseMove={(event) => {
                      if (isReceiverDrawing) drawSignature(event, receiverCanvasRef.current, false);
                    }}
                    onMouseUp={() => setIsReceiverDrawing(false)}
                    onMouseLeave={() => setIsReceiverDrawing(false)}
                    onTouchStart={(event) => {
                      setIsReceiverDrawing(true);
                      drawSignature(event, receiverCanvasRef.current, true);
                    }}
                    onTouchMove={(event) => {
                      if (isReceiverDrawing) drawSignature(event, receiverCanvasRef.current, false);
                    }}
                    onTouchEnd={() => setIsReceiverDrawing(false)}
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => clearCanvas(receiverCanvasRef.current)} className="absolute top-2 right-2 h-8 rounded-lg text-xs font-bold text-gray-500 hover:text-red-500 bg-white shadow-sm border border-gray-100">
                    Clear Pad
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <p className="mr-auto flex items-center gap-2 text-xs text-muted-foreground">
          <Check size={14} className="text-blue-500" />
          The receiving person&apos;s signature is required to complete the handover chain of custody.
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="h-11 rounded-xl px-6 font-bold text-gray-700 bg-white hover:bg-gray-50 border-gray-200"
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="h-11 rounded-xl px-6 font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20"
          disabled={submitting}
        >
          {submitting ? (
            uploadingPhotos ? (
              'Uploading Photos...'
            ) : (
              'Processing Return...'
            )
          ) : (
            <span className="flex items-center gap-2">
              <Save size={18} />
              {submitLabel}
            </span>
          )}
        </Button>
      </div>
    </form>
  );
};

export default AssetReturnForm;
